/**
 * VerificationDialog — Modal for confirming token verification actions.
 *
 * Three variants: verify, flag (requires reason), remove.
 * Shows loading state during mutation, error message on failure.
 * Uses native <dialog> showModal() for focus trapping and backdrop.
 *
 * Story 7.3 — Task 5
 */

import type { Token } from "@repo/shared-types";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";

type VerificationAction = "verify" | "flag" | "remove";

type VerificationDialogProps = {
	open: boolean;
	token: Token | null;
	action: VerificationAction;
	isPending: boolean;
	error: Error | null;
	onConfirm: (reason?: string) => void;
	onCancel: () => void;
};

const DIALOG_CONFIG: Record<
	VerificationAction,
	{ title: string; message: (name: string) => string; confirmLabel: string; confirmClass: string }
> = {
	verify: {
		title: "Verify Token",
		message: (name) => `Are you sure you want to verify "${name}"?`,
		confirmLabel: "Confirm Verify",
		confirmClass: "bg-green-600 text-white hover:bg-green-700",
	},
	flag: {
		title: "Flag Token",
		message: () => "Why is this token being flagged?",
		confirmLabel: "Confirm Flag",
		confirmClass: "bg-yellow-600 text-white hover:bg-yellow-700",
	},
	remove: {
		title: "Remove Verification",
		message: (name) => `This will reset "${name}" to unverified.`,
		confirmLabel: "Confirm Remove",
		confirmClass: "bg-red-600 text-white hover:bg-red-700",
	},
};

export function VerificationDialog({
	open,
	token,
	action,
	isPending,
	error,
	onConfirm,
	onCancel,
}: VerificationDialogProps) {
	const [reason, setReason] = useState("");
	const dialogRef = useRef<HTMLDialogElement>(null);

	// Sync dialog open/close with showModal/close for native focus trap + backdrop
	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) return;

		if (open && !dialog.open) {
			dialog.showModal();
		} else if (!open && dialog.open) {
			dialog.close();
		}
	}, [open]);

	// Reset reason when dialog opens or token changes (H4: prevent stale reason leak)
	useEffect(() => {
		if (open) setReason("");
	}, [open]);

	// Handle native dialog cancel (Escape key)
	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) return;

		const handleCancel = (e: Event) => {
			e.preventDefault();
			onCancel();
		};

		dialog.addEventListener("cancel", handleCancel);
		return () => dialog.removeEventListener("cancel", handleCancel);
	}, [onCancel]);

	const config = DIALOG_CONFIG[action];
	const needsReason = action === "flag";
	const canConfirm = !needsReason || reason.trim().length > 0;

	const handleSubmit = useCallback(
		(e: FormEvent) => {
			e.preventDefault();
			if (!canConfirm) return;
			onConfirm(needsReason ? reason.trim() : undefined);
		},
		[canConfirm, needsReason, reason, onConfirm]
	);

	const handleCancel = useCallback(() => {
		setReason("");
		onCancel();
	}, [onCancel]);

	if (!open || !token) return null;

	return (
		<dialog
			ref={dialogRef}
			aria-labelledby="verification-dialog-title"
			className="mx-4 w-full max-w-md rounded-lg border border-pastel-border bg-pastel-white p-6 shadow-lg backdrop:bg-black/40"
			onClick={(e) => {
				// Close when clicking backdrop (outside dialog content)
				if (e.target === e.currentTarget) handleCancel();
			}}
			onKeyDown={() => {
				/* Escape handled by native cancel event */
			}}
		>
			<form onSubmit={handleSubmit}>
				<h2 id="verification-dialog-title" className="text-lg font-bold text-pastel-black">
					{config.title}
				</h2>
				<p className="mt-2 text-sm text-pastel-gray">{config.message(token.name)}</p>

				{needsReason && (
					<textarea
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						placeholder="Enter reason for flagging..."
						rows={3}
						className="mt-3 w-full rounded-lg border border-pastel-border bg-pastel-white px-3 py-2 text-sm text-pastel-black placeholder:text-pastel-gray focus:outline-none focus:ring-2 focus:ring-pastel-vibrant-blue/40"
					/>
				)}

				{error && <p className="mt-2 text-sm text-red-600">{error.message}</p>}

				<div className="mt-4 flex justify-end gap-2">
					<button
						type="button"
						onClick={handleCancel}
						disabled={isPending}
						className="rounded-lg border border-pastel-border px-4 py-2 text-sm font-medium text-pastel-gray transition-colors hover:bg-pastel-light-gray"
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={!canConfirm || isPending}
						className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${config.confirmClass}`}
					>
						{isPending ? "Processing..." : config.confirmLabel}
					</button>
				</div>
			</form>
		</dialog>
	);
}
